import { useState, useEffect, useCallback } from 'react';

const STATUS_COLORS = { pending: '#f59e0b', processing: '#3b82f6', sent: '#10b981', failed: '#ef4444', cancelled: '#6b7280' };
const TYPE_LABELS = {
  drip: 'Drip', order_confirmation: 'Order Conf.', payment_failure: 'Payment Fail',
  activation: 'Activation', announcement: 'Announcement', verification: 'Verification',
  support_ticket: 'Support', suspension: 'Suspension', plan_upgrade: 'Upgrade',
  plan_downgrade: 'Downgrade', other: 'Other',
};

function PreviewModal({ item, onClose }) {
  if (!item) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
      <div style={{ background: 'var(--admin-surface)', borderRadius: '12px', width: '100%', maxWidth: '780px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--admin-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--admin-text-primary)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.subject}</div>
            <div style={{ fontSize: '11px', color: 'var(--admin-text-muted)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span>To: <span style={{ color: 'var(--admin-text-secondary)' }}>{item.recipient}</span></span>
              <span>Type: <span style={{ color: 'var(--admin-text-secondary)' }}>{TYPE_LABELS[item.email_type] || item.email_type}</span></span>
              <span>Status: <span style={{ color: STATUS_COLORS[item.status] || '#6b7280', fontWeight: '600' }}>{item.status}</span></span>
              <span>Scheduled: <span style={{ color: 'var(--admin-text-secondary)' }}>{new Date(item.scheduled_at).toLocaleString()}</span></span>
            </div>
          </div>
          <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={onClose} style={{ flexShrink: 0 }}>
            <i className="fa-solid fa-xmark" /> Close
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {item.html_body ? (
            <iframe
              srcDoc={item.html_body}
              sandbox="allow-same-origin"
              style={{ width: '100%', height: '560px', border: 'none', background: '#fff' }}
              title="Email Preview"
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '13px' }}>No preview available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminEmailQueue({ apiFetch, toast }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [acting, setActing] = useState({});
  const [bulkActing, setBulkActing] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('email_type', filterType);
      if (filterRecipient) params.append('recipient', filterRecipient);
      const r = await apiFetch(`/api/admin/email-queue?${params}`);
      const d = await r.json();
      setQueue(d.queue || []);
      setSelected(new Set());
    } catch { toast('Failed to load email queue', 'error'); }
    setLoading(false);
  }, [apiFetch, toast, filterStatus, filterType, filterRecipient]);

  useEffect(() => { load(); }, [load]);

  const handleSendNow = async (id) => {
    setActing(a => ({ ...a, [id]: 'sending' }));
    try {
      const r = await apiFetch(`/api/admin/email-queue/${id}/send-now`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      toast('Email sent');
      load();
    } catch (e) { toast(e.message || 'Send failed', 'error'); }
    setActing(a => ({ ...a, [id]: null }));
  };

  const handleCancel = async (id) => {
    setActing(a => ({ ...a, [id]: 'cancelling' }));
    try {
      await apiFetch(`/api/admin/email-queue/${id}/cancel`, { method: 'POST' });
      toast('Cancelled');
      load();
    } catch { toast('Cancel failed', 'error'); }
    setActing(a => ({ ...a, [id]: null }));
  };

  const handleBulkSendNow = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Send ${selected.size} email(s) now?`)) return;
    setBulkActing(true);
    try {
      const r = await apiFetch('/api/admin/email-queue/bulk-send-now', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [...selected] }) });
      const d = await r.json();
      toast(`Sent: ${d.sent}, Failed: ${d.failed}`);
      load();
    } catch { toast('Bulk send failed', 'error'); }
    setBulkActing(false);
  };

  const toggleSelect = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === queue.length ? new Set() : new Set(queue.map(q => q.id)));

  const pendingCount = queue.filter(q => q.status === 'pending').length;
  const failedCount = queue.filter(q => q.status === 'failed').length;

  return (
    <div>
      <PreviewModal item={preview} onClose={() => setPreview(null)} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--admin-text-primary)' }}>Email Queue</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {selected.size > 0 && (
            <button className="admin-btn admin-btn--primary admin-btn--sm" onClick={handleBulkSendNow} disabled={bulkActing}>
              {bulkActing ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-paper-plane" />} Send {selected.size} Now
            </button>
          )}
          <button className="admin-btn admin-btn--secondary admin-btn--sm" onClick={load}>
            <i className="fa-solid fa-rotate-right" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { label: 'Pending', count: pendingCount, color: '#f59e0b' },
          { label: 'Failed', count: failedCount, color: '#ef4444' },
          { label: 'Total', count: queue.length, color: 'var(--admin-text-muted)' },
        ].map(s => (
          <div key={s.label} className="admin-card" style={{ padding: '10px 18px', minWidth: '90px', textAlign: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: s.color }}>{s.count}</div>
            <div style={{ fontSize: '11px', color: 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="admin-select" style={{ fontSize: '12px', padding: '5px 8px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['pending', 'processing', 'sent', 'failed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="admin-select" style={{ fontSize: '12px', padding: '5px 8px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All types</option>
          {Object.keys(TYPE_LABELS).map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        <input className="admin-input" style={{ fontSize: '12px', padding: '5px 8px', width: '180px' }} placeholder="Filter by recipient…" value={filterRecipient} onChange={e => setFilterRecipient(e.target.value)} />
        <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={load}><i className="fa-solid fa-search" /></button>
        {(filterStatus || filterType || filterRecipient) && (
          <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => { setFilterStatus(''); setFilterType(''); setFilterRecipient(''); }}>Clear</button>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--admin-text-muted)', fontSize: '13px', padding: '24px' }}>Loading…</div>
      ) : queue.length === 0 ? (
        <div className="admin-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-text-muted)', fontSize: '14px' }}>
          <i className="fa-solid fa-inbox" style={{ fontSize: '32px', marginBottom: '12px', display: 'block', opacity: 0.4 }} />
          No emails in queue.
        </div>
      ) : (
        <div className="admin-card" style={{ overflow: 'hidden' }}>
          <table className="admin-table" style={{ fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ width: '32px' }}><input type="checkbox" checked={selected.size === queue.length && queue.length > 0} onChange={toggleAll} /></th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th>Scheduled</th>
                <th>Attempts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.map(item => (
                <tr key={item.id} style={{ opacity: item.status === 'cancelled' ? 0.5 : 1 }}>
                  <td><input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} disabled={item.status === 'sent' || item.status === 'cancelled'} /></td>
                  <td style={{ color: 'var(--admin-text-primary)' }}>{item.recipient}</td>
                  <td style={{ color: 'var(--admin-text-secondary)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.subject}>{item.subject}</td>
                  <td><span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '10px', background: 'rgba(255,255,255,0.07)', color: 'var(--admin-text-secondary)' }}>{TYPE_LABELS[item.email_type] || item.email_type}</span></td>
                  <td><span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', background: (STATUS_COLORS[item.status] || '#6b7280') + '22', color: STATUS_COLORS[item.status] || '#6b7280' }}>{item.status}</span></td>
                  <td style={{ color: 'var(--admin-text-muted)', whiteSpace: 'nowrap' }}>{new Date(item.scheduled_at).toLocaleString()}</td>
                  <td style={{ color: item.attempts >= item.max_attempts ? '#ef4444' : 'var(--admin-text-muted)' }}>{item.attempts}/{item.max_attempts}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        className="admin-btn admin-btn--sm admin-btn--ghost"
                        onClick={() => setPreview(item)}
                        title="Preview email"
                      >
                        <i className="fa-solid fa-eye" />
                      </button>
                      {(item.status === 'pending' || item.status === 'failed') && (
                        <button
                          className="admin-btn admin-btn--sm admin-btn--primary"
                          onClick={() => handleSendNow(item.id)}
                          disabled={!!acting[item.id]}
                          title="Send immediately"
                        >
                          {acting[item.id] === 'sending' ? <i className="fa-solid fa-spinner fa-spin" /> : <i className="fa-solid fa-paper-plane" />} Send Now
                        </button>
                      )}
                      {item.status === 'pending' && (
                        <button
                          className="admin-btn admin-btn--sm admin-btn--ghost"
                          onClick={() => handleCancel(item.id)}
                          disabled={!!acting[item.id]}
                          title="Cancel this email"
                        >
                          <i className="fa-solid fa-ban" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
